<ng-template #mediaTpl>
  <div class="d-flex align-items-start flex-column gap-2 m-1 acc-height-100">
    <div class="d-flex align-items-start flex-column gap-2 w-100 h-100 media-upload-wrapper">
      <!-- Preselected files START -->
      <div class="d-flex align-items-start gap-2 w-100">
        <!-- Preview uploads START -->
        <div class="flex-fill p-2">
          <div class="fs-3 ms-2 mt-3 mb-4 d-flex gap-2">
            <div>{{ (filesToUpload.length === 1 ? 'media.upload' : 'media.uploads')|translate }}</div>
          </div>
          @if (filesToUpload.length > 0) {
            <div class="d-flex flex-wrap gap-2">
              @for (item of filesToUpload; track $index; let idx = $index) {
                <div class="acc-w-32">
                  <ng-container *ngTemplateOutlet="uploadPreview; context{item,idx}"/>
                </div>
              }
            </div>
          }
        </div>
        <!-- Preview uploads END -->
        @if (filesToUpload.length > 0) {
          <!-- All uploads settings START -->
          <div class="ms-auto p-2 upload-action-wrapper border-start">
            <div class="fs-3 ms-2 mt-3 mb-4"> {{ 'media.files.all'|translate }}</div>
            <div>
              <ng-container *ngTemplateOutlet="uploadAction"/>
            </div>
          </div>
          <!-- All uploads settings END -->
        }
      </div>
      <!-- Preselected files END -->
      <!-- Drag and Drop Zone START -->
      <div class="box d-flex justify-content-center align-items-center file-drop-zone m-1 w-100 h-100"
           [class.dragover]="isDragOver"
           (click)="fileInput.click()" (keydown.enter)="fileInput.click()" tabindex="0"
           (dragover)="onDragOver($event)" (dragleave)="onDragLeave($event)" (drop)="onDrop($event)">
        <div class="h-10 w-75 text-center">
          <div class="w-100 h-100 d-flex flex-column justify-content-center">
            <p>{{ 'media.upload.drag.info'|translate }}</p>
            <p>{{ 'media.upload.supported.fileFormat'|translate }}</p>
            <p>{{ getSupportedFileFormats().join(', ') }}</p>
            <input type="file" class="hidden" (change)="onFileSelect($event)" #fileInput multiple>
          </div>
        </div>
      </div>
      <!-- Drag and Drop Zone END -->
    </div>
    <!-- Footer START -->
    <div class="mt-auto w-100">
      <acc-card>
        <acc-card-body>
          <acc-action-bar>
            @if (filesToUpload.length > 0) {
              <acc-button status="primary" (accClick)="uploadAll()" [loadingTimeout]="-1"
                          [disabled]="getPendingCount() === 0">
                @if (uploading) {
                  {{ 'wait' |translate }}
                } @else {
                  {{ (filesToUpload.length === 1 ? 'media.upload' : 'media.uploads')|translate }}
                }
              </acc-button>
            }
            @if (!uploading) {
              <acc-button class="ms-auto" status="outline-gray" (accClick)="close()">
                {{ 'modal.close'|translate }}
              </acc-button>
            }
          </acc-action-bar>
        </acc-card-body>
      </acc-card>
    </div>
    <!-- Footer END -->
  </div>
</ng-template>

<ng-template #uploadPreview let-item="item" let-idx="idx">
  <div class="d-flex">
    <!-- Preview image START -->
    <div class="me-2 ms-1 upload-preview">
      @if (item.previewUrl) {
        <img [src]="item.previewUrl" class="img-thumbnail w-100 h-100 object-fit-cover" alt="preview">
      } @else {
        <div class="upload-preview-placeholder w-100 h-100">
          <acc-icon [name]="getIconForType(item.file.type)" [size]="3"/>
        </div>
      }
    </div>
    <!-- Preview image END -->

    <div class="flex-fill d-flex flex-column gap-1 overflow-hidden">
      <!-- Filename START -->
      <div class="fw-bold text-truncate " [title]="item.file.name"
           [class.text-danger]="item.status === 'error'">
        {{ item.file.name }}
      </div>
      <!-- Filename START -->
      @if (item.status === 'uploading') {
        <div class="text-info">{{ 'media.upload.progress'|translate }}</div>
      } @else if (item.status === 'success') {
        <div class="text-success">{{ 'media.upload.success'|translate }}</div>
      } @else {
        <!-- Tags START -->
        <div>
          @if (item.status === 'error') {
            <div class="text-danger">{{ 'media.upload.error'|translate }}</div>
          } @else {
            <div class="d-flex align-items-center gap-2">
              @if (isAllowAttachment(item)) {
                <div class="form-check form-switch">
                  <input [id]="'item-attachment-' + idx" type="checkbox" class="form-check-input"
                         [(ngModel)]="item.attachment">
                  <label class="form-label form-check-label text-nowrap" [for]="'item-attachment-' + idx">
                    {{ 'media.upload.attachment'|translate }}
                    <acc-icon name="info" styling="success" [ngbTooltip]="'media.upload.attachment.info'|translate"/>
                  </label>
                </div>
              }
              <div>
                <input type="text" class="form-control form-control-sm" [(ngModel)]="item.tags" maxlength="200"
                       [placeholder]="'media.upload.tags.placeholder'|translate"
                       [disabled]="item.status === 'uploading' || uploading">
              </div>
            </div>
          }
        </div>
        <!-- Tags END -->
          <!-- Remove button START -->
        <div>
          @if (!uploading && item.status !== 'error') {
            <acc-button status="outline-danger" size="sm"
                        [loadingTimeout]="0" (accClick)="removeFile(idx)">
              <acc-icon name="cancel"/>
              {{ 'media.upload.remove'|translate }}
            </acc-button>
          }
        </div>
        <!-- Remove button END -->
      }
    </div>
  </div>
</ng-template>

<ng-template #uploadAction>
  <div class="flex-fill d-flex flex-column gap-1">
    <div>
      <label for="all-tags">{{ 'media.upload.tags'|translate }}</label>
      <input id="all-tags" type="text" class="form-control form-control-sm" maxlength="200"
             [placeholder]="'media.upload.tags.placeholder'|translate"
             [disabled]="uploading" [(ngModel)]="allTags">
    </div>
    <div>
      <label for="all-deletion-protection">{{ 'media.upload.deletion.protection'|translate }}</label>
      <select id="all-deletion-protection" class="form-control form-select form-select-sm shadow-none"
              [disabled]="uploading" [(ngModel)]="allDeletionProtection">
        @for (opt of getDeletionProtectionOptions(); track opt) {
          <option [value]="opt"> {{ ('deletion.protection.' + (opt|lowercase))|translate }}</option>
        }
      </select>
    </div>
    <div class="mt-3">
      <acc-button status="primary" size="sm" accClass="w-100" (accClick)="uploadAll()" [loadingTimeout]="-1"
                  [disabled]="getPendingCount() === 0">
        @if (uploading) {
          {{ 'wait' |translate }}
        } @else {
          {{ (filesToUpload.length === 1 ? 'media.upload' : 'media.uploads')|translate }}
        }
      </acc-button>
    </div>
    <div class="mt-3">
      @if (allSuccess()) {
        <acc-button status="outline-gray" size="sm" accClass="w-100" (accClick)="close()">
          {{ 'modal.close'|translate }}
        </acc-button>
      } @else if (!uploading) {
        <acc-button status="outline-danger" size="sm" accClass="w-100"
                    [loadingTimeout]="0" (accClick)="clean()">
          <acc-icon name="cancel"/>
          {{ 'media.upload.removeAll'|translate }}
        </acc-button>
      }
    </div>
  </div>
</ng-template>
